import { z } from '@plt/types'
import type {
  DepartureTiming,
  StructuredConditionalTaxCode,
  StructuredFareRules,
  StructuredPctPenaltyTier,
} from './types'

/**
 * Structured Cat 16/31/33 rules — the machine-readable replacement for regex-parsing
 * airline-authored rule prose. Rules are extracted once per (airline, fareBasis, text) by
 * tmp/extract-fare-rules.ts (offline/batch) and validated against this schema, so the core
 * calculator never depends on an LLM call at quote time.
 *
 * Exception: Refunds.tsx's "AI Assisted Quote" button calls a live extraction endpoint on demand
 * (human-triggered) and writes the result in the same shape — a deliberate opt-in exception to the
 * "never live" rule, mirroring its existing exception for computeOpsRefundQuote.
 *
 * Rollout contract: every resolver falls back to the regex parser when it can't answer, so
 * attaching structuredFareRules can only ADD information — diff with tmp/shadow-compare.ts first.
 */

export const StructuredFareRulesSchema = z.object({
  originDestination: z.string(),
  fareBasis: z.string(),
  airline: z.string(),
  refundable: z
    .boolean()
    .describe(
      'false ONLY if the fare is unconditionally non-refundable for a voluntary cancellation before departure (e.g. "TICKET IS NON-REFUNDABLE" with no cancel/refund charge). If a refund is permitted against a fee, this is true.',
    ),
  cancellationPenalties: z
    .array(
      z.object({
        timing: z.enum(['BEFORE_DEPARTURE', 'AFTER_DEPARTURE']),
        amount: z.number().nonnegative(),
        currency: z.string().length(3).describe('3-letter currency code as filed'),
        minHoursBeforeDeparture: z
          .number()
          .nonnegative()
          .optional()
          .describe(
            'Set ONLY when the SAME timing window (e.g. BEFORE_DEPARTURE) is filed as multiple OR-branches with different fees keyed to a departure-proximity cutoff — e.g. "...24 HOURS BEFORE SCHEDULED DEPARTURE TIME... CHARGE 300" as one branch and a sibling OR-branch with no such cutoff charging a different amount for the same BEFORE_DEPARTURE window. Use the stated hour cutoff (24 HOURS BEFORE SCHEDULED DEPARTURE TIME = 24). Omit for a plain single BEFORE_DEPARTURE/AFTER_DEPARTURE fee with no such sub-branch.',
          ),
      }),
    )
    .describe(
      'Fixed cancellation/refund fees for VOLUNTARY cancellation, one entry per distinct (timing, minHoursBeforeDeparture) branch. Use the fee that applies per passenger/ticket. Do not include change/reissue fees (Cat 31) here. Do not include an issuance-based waiver here — that goes in issuanceWaiver.',
    ),
  pctPenaltyTiers: z
    .array(
      z.object({
        beforeDays: z.number().nonnegative(),
        pct: z.number().min(0).max(100),
      }),
    )
    .describe(
      'Percentage-of-fare penalty tiers by days before departure, e.g. "BEFORE 30 DAYS TO DEPARTURE - PENALTY APPLIES PCT 25 OF THE FARE". beforeDays = the tier applies when cancelling at least that many days out.',
    ),
  issuanceWaiver: z
    .object({
      withinHours: z
        .number()
        .positive()
        .describe('The waiver applies when the refund is requested within this many hours of ticket issuance.'),
      amount: z.number().nonnegative().describe('The fee that applies inside the waiver window — usually 0 (free).'),
      currency: z.string().length(3).describe('3-letter currency code as filed'),
    })
    .optional()
    .describe(
      'A refund rule keyed to time since TICKET ISSUANCE rather than departure — e.g. "REFUND MUST BE MADE PRIOR TO 24 HOURS AFTER TICKET ISSUE DATE... NO CHARGE." This is a DIFFERENT axis from cancellationPenalties (which is keyed to departure timing): the issuance waiver is evaluated first and, when satisfied, overrides whatever cancellationPenalties would otherwise apply. Omit entirely if the text states no such issuance-based waiver — do not invent one from the general BEFORE/AFTER DEPARTURE fee lines, and do not confuse it with a generic 24-hour-before-departure cutoff (that belongs in cancellationPenalties.minHoursBeforeDeparture instead).',
    ),
  nonRefundableTaxCodes: z
    .array(z.string().length(2))
    .describe(
      'Two-letter tax codes the rules declare non-refundable on a voluntary refund in EVERY situation (e.g. "REFUND OF YQ NOT PERMITTED"). A code non-refundable only in some situation (non-refundable tickets, partly used tickets, late cancellation, no-show, base fare below the penalty) goes in conditionalNonRefundableTaxCodes instead, never here. Only codes explicitly stated; do not infer.',
    ),
  conditionalNonRefundableTaxCodes: z
    .array(
      z.object({
        code: z.string().length(2),
        condition: z
          .enum([
            'NON_REFUNDABLE_TICKET',
            'BASE_FARE_NOT_ABOVE_PENALTY',
            'TICKET_PARTLY_USED',
            'WITHIN_HOURS_OF_DEPARTURE',
            'NO_SHOW',
            'OTHER',
          ])
          .describe(
            'NON_REFUNDABLE_TICKET: only on a non-refundable ticket/fare. BASE_FARE_NOT_ABOVE_PENALTY: the whole code is withheld when the base fare is equal to or lower than the penalty. TICKET_PARTLY_USED: only when the ticket is partly used, used out of sequence, or not fully unutilised. WITHIN_HOURS_OF_DEPARTURE: only when cancelled less than `hours` before departure. NO_SHOW: only after a no-show. OTHER: any other condition — explain it in notes.',
          ),
        hours: z
          .number()
          .positive()
          .optional()
          .describe(
            'WITHIN_HOURS_OF_DEPARTURE only: the stated cutoff, e.g. "LESS THAN 24 HOURS PRIOR TO DEPARTURE" = 24.',
          ),
      }),
    )
    .optional()
    .describe(
      'Tax codes the rules make non-refundable ONLY in a stated situation, one entry per (code, condition) — a code withheld in two situations gets two entries. Leave out codes listed in nonRefundableTaxCodes.',
    ),
  penaltyBasis: z
    .enum(['PER_TICKET', 'PER_FARE_COMPONENT'])
    .optional()
    .describe(
      'Whether the cancellation fee is charged once per ticket/passenger or once per fare component. If the text says "per pricing unit", "per ticket", or "collect highest", use PER_TICKET. Default PER_TICKET when unstated.',
    ),
  sourceCategory: z
    .enum(['16', '31', '33', 'COMBINED'])
    .optional()
    .describe(
      'Which category the refund fee was sourced from. Cat 33 (VOLUNTARY REFUNDS) is authoritative over Cat 16 (PENALTIES) when both state a refund fee.',
    ),
  /**
   * True when the filed text has a conditional clause the extraction flagged as unverifiable
   * from this text alone (e.g. a refund reduction gated on comparing against a live applicable
   * fare) — cancellationPenalties/nonRefundableTaxCodes above may understate what the airline
   * actually withholds. Optional here (old cached extractions predate this field) but required
   * on FareRulesExtractionSchema below — a model allowed to omit it will omit it on exactly the
   * cases that matter.
   */
  hasUnverifiablePenaltyClause: z
    .boolean()
    .optional()
    .describe(
      'true if the text contains a CONDITIONAL clause that could further reduce the refund based on data not present in this text — e.g. "IF APPLICABLE OW/RT FARE PLUS CANCELLATION FEE EXCEEDS THE COLLECTED BASE FARE, THE BASE FARE REMAINS NON-REFUNDABLE" or any comparison against a live/applicable/published fare, a residual-value calculation, or a similar external-data-dependent trigger. This is true REGARDLESS of whether you can tell if the condition is met — the point is flagging that cancellationPenalties/nonRefundableTaxCodes above may not be the final word. Explain the specific clause in notes. false when the filed penalty is a flat, unconditional fee.',
    ),
  notes: z
    .string()
    .optional()
    .describe(
      'Short note on anything ambiguous: conflicting categories, waivers, no-show rules, conditions that could change the fee. If hasUnverifiablePenaltyClause is true, quote or closely paraphrase the triggering clause here.',
    ),
}) satisfies z.ZodType<StructuredFareRules>

/**
 * Extraction-time schema: same fields as StructuredFareRulesSchema minus the identity fields the
 * caller already knows (originDestination/fareBasis/airline — stamped from the input, never
 * trusted from the model), with conditionalNonRefundableTaxCodes/penaltyBasis/sourceCategory/hasUnverifiablePenaltyClause/notes
 * promoted to required so the model can't silently skip them. Shared by tmp/extract-fare-rules.ts
 * (offline/batch) and the live "AI Assisted Quote" endpoint so both stay in sync.
 */
export const FareRulesExtractionSchema = StructuredFareRulesSchema.omit({
  originDestination: true,
  fareBasis: true,
  airline: true,
}).required({
  conditionalNonRefundableTaxCodes: true,
  penaltyBasis: true,
  sourceCategory: true,
  hasUnverifiablePenaltyClause: true,
  notes: true,
})

export const FARE_RULES_EXTRACTION_SYSTEM_PROMPT = `You are an IATA/ATPCO fare-rules analyst extracting VOLUNTARY CANCELLATION refund rules from airline-filed category text.

Rules of interpretation:
- Decide WHETHER the fare is refundable from Category 16 (PENALTIES), not from Category 33.
  - Category 33 (VOLUNTARY REFUNDS) is often filed as alternative "OR -" branches covering every fare it is attached to, e.g. one branch "REFUND ... NO CHARGE" and another "FARE IS NONREFUNDABLE AND TAXES ARE NONREFUNDABLE". Those branches are alternatives; Category 33 alone does not tell you which one applies to this fare, so never let one of them alone decide refundability.
  - Category 16 decides it. Refundable = true when Category 16 permits a voluntary cancellation or refund before departure, e.g. "CANCELLATIONS PERMITTED", "CHANGES/CANCELLATIONS PERMITTED", "CHARGE <CCY> <amount> FOR CANCEL/REFUND", "FULL REFUND ... OF REFUNDABLE FARE TICKETS". Use the Category 16 cancellation fee; if Category 16 permits refund and states no fee, use the fee of the Category 33 refund branch ("NO CHARGE" = 0).
  - Refundable = false when Category 16 says the fare or ticket is non-refundable for a voluntary cancellation, or when Category 16 only provides for changes/reissue, no-show or loss of fare and has no cancellation or refund provision at all — even if a Category 33 branch says "NO CHARGE".
  - When Category 16 permits a refund with a fee but a Category 33 branch says "FARE IS NONREFUNDABLE", the Category 16 fee wins: that Category 33 branch belongs to a different fare.
  - An explicit Category 16 statement that the ticket or fare is non-refundable for a voluntary cancellation/refund, or that refunds are not permitted, always means refundable = false — even when the same text lets the passenger keep the value as a credit, voucher or travel bank.
  - Otherwise, refundability is about whether a voluntary cancellation or refund is permitted, not the form the money takes. When Category 16 permits cancellation, refundable = true even if the residual value is returned as a credit certificate, voucher, travel bank or EMD; say the form in notes. Using the fare value towards a new ticket (a reissue or change) is a change, not a refund: it never makes a fare refundable.
  - When Category 16 carries no rule content at all (e.g. only "CONTACT CARRIER FOR DETAILS") you cannot tell: set refundable = true, leave cancellationPenalties empty, and say in notes that refundability is unknown. Do not treat missing information as non-refundable.
  - Only when there is no Category 16 text at all, decide from Category 33, and say so in notes.
- Category 31 is VOLUNTARY CHANGES — never use its fees as cancellation fees, but its text can confirm refundability context.
- A free or cheaper refund allowed only within a short time of booking or ticketing (e.g. "REFUNDABLE WITHIN 24 HOURS OF TICKETING") is an issuance waiver: put it in issuanceWaiver. It does NOT make the fare refundable — set refundable from the rule that applies after that window.
- "CHARGE <CCY> <amount> FOR CANCEL/REFUND" is a fixed refund fee.
- A NO-SHOW fee is NOT a cancellation fee. "CHARGE <CCY> <amount> FOR NO-SHOW" never belongs in cancellationPenalties — not under AFTER_DEPARTURE, not anywhere. Nothing in the text tells you whether a no-show occurred, so note it and leave it out of the numbers. Same for reissue/change/upgrade fees.
- "NON-REFUNDABLE" phrases are often section-scoped (e.g. only AFTER DEPARTURE or only NO-SHOW). Mark the fare non-refundable ONLY when no voluntary before-departure refund path exists at all.
- "conditionsApply"/"CHARGE 0" style entries with restrictive text mean the fee is elsewhere in the text, not that cancellation is free.
- Extract only what the text states. Put uncertainty in notes, not in the numbers.
- Watch for TWO DISTINCT temporal axes and never conflate them:
  1. Time since TICKET ISSUANCE (e.g. "REFUND MUST BE MADE PRIOR TO 24 HOURS AFTER TICKET ISSUE DATE... NO CHARGE") — this is an issuance-based waiver: put it in issuanceWaiver, never in cancellationPenalties.
  2. Time before scheduled DEPARTURE. A single BEFORE_DEPARTURE (or AFTER_DEPARTURE) window can itself be filed as multiple OR-branches with different fees depending on a departure-proximity cutoff — e.g. one branch requires "...24 HOURS BEFORE SCHEDULED DEPARTURE TIME" and charges one amount, a sibling "OR -" branch has no such cutoff and charges a different amount, both still labeled as being before departure. When you see this, emit separate cancellationPenalties entries for the same timing with different minHoursBeforeDeparture values (the branch with the stated cutoff gets that hour value; the branch with no cutoff omits minHoursBeforeDeparture) rather than picking just one or averaging/guessing.
- Tax codes (YQ, YR and others) are often non-refundable only in some situations, e.g. "FOR NON-REFUNDABLE TICKETS THE YQ/YR WILL NOT BE REFUNDED", "YR WILL ONLY BE REFUNDABLE FOR FULLY UNUTILIZED TICKETS", "YR IS NON REFUNDABLE INCASE BASE FARE IS EQUAL OR LOWER THAN PENALTIES", "CANCELLATIONS LESS THAN 24 HOURS PRIOR TO DEPARTURE AND NO-SHOWS ... YQ/YR WILL NOT BE REFUNDED". Put each such code in conditionalNonRefundableTaxCodes with its condition, one entry per condition, and NOT in nonRefundableTaxCodes. List a code in nonRefundableTaxCodes only when the text withholds it with no condition attached. These conditions are checked from booking data, so they do not by themselves make hasUnverifiablePenaltyClause true.
  - Use BASE_FARE_NOT_ABOVE_PENALTY only when the WHOLE code is withheld. A clause that deducts only the part of the cancellation fee above the fare from YQ/YR (e.g. "ANY DIFFERENCE BETWEEN CANCELLATION PENALTY AND THE ORIGINALLY PAID FARE SHALL BE DEDUCTED FROM THE YQ/YR AMOUNT", "THE REMAINING PENALTY AMOUNT MUST BE DEDUCTED FROM THE YQ/YR") makes no code non-refundable: the fee is already deducted in full, so leave it out of both tax lists.
  - A ticket used out of sequence or beyond the point of turnaround is partly used: use TICKET_PARTLY_USED, not OTHER.
- Actively look for CONDITIONAL clauses that gate a further refund reduction on data this text doesn't contain — most commonly phrased as "IF [APPLICABLE/PUBLISHED] FARE PLUS [CANCELLATION FEE/PENALTY] EXCEED[S] THE [ACTUAL] COLLECTED [BASE] FARE" (often titled something like "CALCULATION OF YQ/YR FOR REFUNDABLE FARE WITH FEE"). These need a live/applicable fare figure to evaluate that you don't have — set hasUnverifiablePenaltyClause=true whenever you see this pattern, independent of whether cancellationPenalties/nonRefundableTaxCodes above look complete without it.`

/**
 * Picks the applicable penalty among a fare component's OR-branches for one timing window.
 * Entries sharing a timing are alternate filings keyed to departure proximity, NOT a "collect
 * highest" set. Takes the most specific tier whose threshold is met; tiers with no threshold are
 * the catch-all. Falls back to highest-amount-wins when hoursBeforeDeparture is unknown.
 */
function pickPenaltyForTiming(
  penalties: StructuredFareRules['cancellationPenalties'],
  timing: DepartureTiming,
  hoursBeforeDeparture: number | undefined,
): StructuredFareRules['cancellationPenalties'][number] | null {
  const candidates = penalties.filter(p => p.timing === timing)
  if (candidates.length === 0) return null
  if (candidates.length === 1 || hoursBeforeDeparture == null) {
    return candidates.reduce((max, p) => (p.amount > max.amount ? p : max))
  }

  const eligible = candidates.filter(
    p => p.minHoursBeforeDeparture == null || hoursBeforeDeparture >= p.minHoursBeforeDeparture,
  )
  if (eligible.length === 0) return null

  return eligible.reduce((best, p) => {
    const bestThreshold = best.minHoursBeforeDeparture ?? -1
    const pThreshold = p.minHoursBeforeDeparture ?? -1
    return pThreshold > bestThreshold ? p : best
  })
}

/**
 * Cancellation penalty in booking currency for the given timing — same convention as
 * parseCat16CancellationPenalty: pick the applicable branch within a component, then max across
 * components (EK files "COLLECT HIGHEST" per pricing unit). Null when none is usable.
 * Omit hoursBeforeDeparture to keep highest-amount-wins.
 */
export function resolveStructuredCancellationPenalty(
  rules: StructuredFareRules[] | undefined,
  bookingCurrency: string,
  fareToBookingCurrencyRate: number | undefined,
  timing: DepartureTiming,
  hoursBeforeDeparture?: number,
): number | null {
  if (!rules?.length) return null

  let overallMax: number | null = null
  for (const rule of rules) {
    const picked = pickPenaltyForTiming(rule.cancellationPenalties, timing, hoursBeforeDeparture)
    if (!picked) continue

    let amount: number
    // Zero is zero in every currency — a free cancellation must not be dropped just because no FX
    // rate is on file, or "CHARGE 0" reads as "no penalty data" and the ticket looks non-refundable.
    if (picked.amount === 0 || picked.currency.toUpperCase() === bookingCurrency.toUpperCase()) {
      amount = picked.amount
    } else if (fareToBookingCurrencyRate != null && fareToBookingCurrencyRate > 0) {
      amount = Math.round(picked.amount * fareToBookingCurrencyRate)
    } else {
      continue
    }

    if (overallMax === null || amount > overallMax) overallMax = amount
  }

  return overallMax
}

/**
 * Fee that applies when a refund is requested within an airline's issuance-based waiver window
 * (e.g. "REFUND MUST BE MADE PRIOR TO 24 HOURS AFTER TICKET ISSUE DATE... NO CHARGE") — a
 * different axis from departure-based cancellationPenalties, evaluated first. Returns null when
 * hoursSinceIssuance is unknown, no rule carries an issuanceWaiver, or the waiver window has
 * passed — callers should fall through to resolveStructuredCancellationPenalty in that case.
 */
export function resolveStructuredIssuanceWaiver(
  rules: StructuredFareRules[] | undefined,
  bookingCurrency: string,
  fareToBookingCurrencyRate: number | undefined,
  hoursSinceIssuance: number | undefined,
): number | null {
  if (!rules?.length || hoursSinceIssuance == null) return null

  for (const rule of rules) {
    const waiver = rule.issuanceWaiver
    if (!waiver || hoursSinceIssuance > waiver.withinHours) continue

    // amount === 0 short-circuits the FX check: zero converts to zero in any currency, and the
    // waiver is free far more often than not (QF/HAAFAO files "no charge" in FJD against AUD
    // bookings that carry no rate).
    if (waiver.amount === 0 || waiver.currency.toUpperCase() === bookingCurrency.toUpperCase()) return waiver.amount
    if (fareToBookingCurrencyRate != null && fareToBookingCurrencyRate > 0) {
      return Math.round(waiver.amount * fareToBookingCurrencyRate)
    }
  }

  return null
}

/** Union of pct tiers across components, sorted most-lenient first — mirrors parseCat16PctPenaltyTiers. */
export function resolveStructuredPctTiers(rules: StructuredFareRules[] | undefined): StructuredPctPenaltyTier[] {
  if (!rules?.length) return []
  const seen = new Map<string, StructuredPctPenaltyTier>()
  for (const rule of rules) {
    for (const tier of rule.pctPenaltyTiers) seen.set(`${tier.beforeDays}:${tier.pct}`, tier)
  }
  return [...seen.values()].sort((a, b) => b.beforeDays - a.beforeDays)
}

/**
 * True when the fare is unconditionally non-refundable. Mirrors the regex parser's conservatism:
 * a component counts only when it carries no penalty data at all — a "non-refundable" phrase next
 * to a cancellation charge is section-scoped, not a blanket rule.
 */
export function resolveStructuredIsNonRefundable(rules: StructuredFareRules[] | undefined): boolean | null {
  if (!rules?.length) return null
  return rules.some(r => !r.refundable && r.cancellationPenalties.length === 0 && r.pctPenaltyTiers.length === 0)
}

/**
 * True when EVERY fare component is affirmatively refundable. Mirror image of
 * resolveStructuredIsNonRefundable, whose `false` is indistinguishable from "no rules" — so a fare
 * the airline refunds for free used to fall through to NON_REFUNDABLE on Sabre's isRefundable:false
 * alone. Conservative: one restricted component withholds the assumption.
 */
export function resolveStructuredIsRefundable(rules: StructuredFareRules[] | undefined): boolean | null {
  if (!rules?.length) return null
  return rules.every(r => r.refundable)
}

/** Union of non-refundable tax codes across components; null when no structured rules exist. */
export function resolveStructuredNonRefundableTaxCodes(rules: StructuredFareRules[] | undefined): Set<string> | null {
  if (!rules?.length) return null
  const codes = new Set<string>()
  for (const rule of rules) {
    for (const code of rule.nonRefundableTaxCodes) codes.add(code.toUpperCase())
  }
  return codes
}

/**
 * Every component's conditional tax codes, upper-cased and de-duplicated. Empty when no reading
 * carries the field (readings made before it existed list everything as unconditional).
 */
export function resolveStructuredConditionalTaxCodes(
  rules: StructuredFareRules[] | undefined,
): StructuredConditionalTaxCode[] {
  const seen = new Map<string, StructuredConditionalTaxCode>()
  for (const rule of rules ?? []) {
    for (const entry of rule.conditionalNonRefundableTaxCodes ?? []) {
      const code = entry.code.toUpperCase()
      seen.set(`${code}:${entry.condition}:${entry.hours ?? ''}`, { ...entry, code })
    }
  }
  return [...seen.values()]
}

/**
 * True when any component carries a clause the extraction couldn't verify from the text alone —
 * the quoted fees may understate what the airline withholds. Drives the
 * 'unverifiable-penalty-clause' eligibility gate.
 */
export function resolveStructuredHasUnverifiablePenaltyClause(rules: StructuredFareRules[] | undefined): boolean {
  return !!rules?.some(r => r.hasUnverifiablePenaltyClause)
}
